import axios from 'axios';

export type OntologyEnvironment = 'production' | 'testing';
export type OntologyEndpointType =
  | 'health'
  | 'propertyList'
  | 'classList'
  | 'graphClassCount'
  | 'graphTripleCount';

const ontologyProjects = {
  easycycle: 'easycycle',
  tsgc: 'tsgc',
  nmtl: 'nmtl',
  hkbdb: 'hkbdb',
  lxt: 'lxt',
  scada: 'scada',
  tlit: 'tlit',
  dpp: 'dpp',
  carbonFootprint: 'carbonFootprint',
  land: 'land',
} as const;

type OntologyProjectId = keyof typeof ontologyProjects;

type OntologyBaseMapping = {
  [projectId in OntologyProjectId]: {
    production?: string;
    testing?: string;
  }
};

type OntologyPathRule = {
  useLangPath: boolean;
  usePublicPrefix: boolean;
};

const safeBase = (value?: string): string => (value || '').replace(/\/$/, '');

const resolveBaseByEnvironment = (
  envSpecific: { production?: string; testing?: string },
  fallbackBase?: string,
): { production: string; testing: string } => {
  const fallback = safeBase(fallbackBase);
  const production = safeBase(envSpecific.production) || fallback;
  const testing = safeBase(envSpecific.testing) || fallback;
  return {
    production,
    testing,
  };
};
//
const langPath = process.env.REACT_APP_EASY_API_LANG || 'en';
//
const easyApiBase = process.env.REACT_APP_EASY_API_NODE;
const tsgcApiBase = process.env.REACT_APP_TSGC_API_NODE;
const nmtlApiBase = process.env.REACT_APP_NMTL_API_NODE;
const hkbdbApiBase = process.env.REACT_APP_HKBDB_API_NODE;
const lxtApiBase = process.env.REACT_APP_LXT_API_NODE;
const scadaApiBase = process.env.REACT_APP_SCADA_API_NODE;
const tlitApiBase = process.env.REACT_APP_TLIT_API_NODE;
const dppApiBase = process.env.REACT_APP_DPP_API_NODE;
const carbonFootprintApiBase = process.env.REACT_APP_CARBON_FOOTPRINT_API_NODE;
const landApiBase = process.env.REACT_APP_LAND_API_NODE;

const environmentApiBases: OntologyBaseMapping = {
  easycycle: resolveBaseByEnvironment(
    {
      production: process.env.REACT_APP_EASY_API_NODE_PRODUCTION,
      testing: process.env.REACT_APP_EASY_API_NODE_TESTING,
    },
    easyApiBase,
  ),
  tsgc: resolveBaseByEnvironment(
    {
      production: process.env.REACT_APP_TSGC_API_NODE_PRODUCTION,
      testing: process.env.REACT_APP_TSGC_API_NODE_TESTING,
    },
    tsgcApiBase,
  ),
  nmtl: resolveBaseByEnvironment(
    {
      production: process.env.REACT_APP_NMTL_API_NODE_PRODUCTION,
      testing: process.env.REACT_APP_NMTL_API_NODE_TESTING,
    },
    nmtlApiBase,
  ),
  hkbdb: resolveBaseByEnvironment(
    {
      production: process.env.REACT_APP_HKBDB_API_NODE_PRODUCTION,
      testing: process.env.REACT_APP_HKBDB_API_NODE_TESTING,
    },
    hkbdbApiBase,
  ),
  lxt: resolveBaseByEnvironment(
    {
      production: process.env.REACT_APP_LXT_API_NODE_PRODUCTION,
      testing: process.env.REACT_APP_LXT_API_NODE_TESTING,
    },
    lxtApiBase,
  ),
  scada: resolveBaseByEnvironment(
    {
      production: process.env.REACT_APP_SCADA_API_NODE_PRODUCTION,
      testing: process.env.REACT_APP_SCADA_API_NODE_TESTING,
    },
    scadaApiBase,
  ),
  tlit: resolveBaseByEnvironment(
    {
      production: process.env.REACT_APP_TLIT_API_NODE_PRODUCTION,
      testing: process.env.REACT_APP_TLIT_API_NODE_TESTING,
    },
    tlitApiBase,
  ),
  dpp: resolveBaseByEnvironment(
    {
      production: process.env.REACT_APP_DPP_API_NODE_PRODUCTION,
      testing: process.env.REACT_APP_DPP_API_NODE_TESTING,
    },
    dppApiBase,
  ),
  carbonFootprint: resolveBaseByEnvironment(
    {
      production: process.env.REACT_APP_CARBON_FOOTPRINT_API_NODE_PRODUCTION,
      testing: process.env.REACT_APP_CARBON_FOOTPRINT_API_NODE_TESTING,
    },
    carbonFootprintApiBase,
  ),
  land: resolveBaseByEnvironment(
    {
      production: process.env.REACT_APP_LAND_API_NODE_PRODUCTION,
      testing: process.env.REACT_APP_LAND_API_NODE_TESTING,
    },
    landApiBase,
  ),
};

const ontologyPathRules: { [projectId in OntologyProjectId]: OntologyPathRule } = {
  easycycle: { useLangPath: true, usePublicPrefix: true },
  tsgc: { useLangPath: true, usePublicPrefix: false },
  nmtl: { useLangPath: true, usePublicPrefix: false },
  hkbdb: { useLangPath: true, usePublicPrefix: false },
  lxt: { useLangPath: false, usePublicPrefix: false },
  scada: { useLangPath: false, usePublicPrefix: false },
  tlit: { useLangPath: true, usePublicPrefix: false },
  dpp: { useLangPath: true, usePublicPrefix: false },
  carbonFootprint: { useLangPath: true, usePublicPrefix: false },
  land: { useLangPath: true, usePublicPrefix: false },
};

const buildProjectApiPrefix = (projectId: OntologyProjectId, environment: OntologyEnvironment): string => {
  const base = safeBase(environmentApiBases[projectId]?.[environment]);
  if (!base) {
    return '';
  }
  const rules = ontologyPathRules[projectId];
  if (rules.usePublicPrefix) {
    return `${base}/public/${langPath}`;
  }
  if (rules.useLangPath) {
    return `${base}/${langPath}`;
  }
  return base;
};

const endpointSuffixByProject: {
  [projectId in OntologyProjectId]: {
    health: string;
    propertyList: string;
    classList: string;
    graphClassCount: string | null;
    graphTripleCount: string | null;
  }
} = {
  easycycle: {
    health: '/health',
    propertyList: '/ontology/protege/list/1.0?limit=-1&offset=0',
    classList: '/ontology/class/list/1.0?limit=-1&offset=0',
    graphClassCount: '/ontology/graph/class/count/1.0?limit=-1&offset=0',
    graphTripleCount: '/ontology/graph/triple/count/1.0?limit=-1&offset=0',
  },
  tsgc: {
    health: '/health',
    propertyList: '/ontology/protege/list/1.0?limit=-1&offset=0',
    classList: '/ontology/class/list/1.0?limit=-1&offset=0',
    graphClassCount: '/ontology/graph/class/count/1.0?limit=-1&offset=0',
    graphTripleCount: '/ontology/graph/triple/count/1.0?limit=-1&offset=0',
  },
  nmtl: {
    health: '/health',
    propertyList: '/ontology/protege/list/1.1?limit=-1&offset=0',
    classList: '/ontology/class/list/1.0?limit=-1&offset=0',
    graphClassCount: null,
    graphTripleCount: null,
  },
  hkbdb: {
    health: '/health',
    propertyList: '/ontology/protege/list/2.0?limit=-1&offset=0',
    classList: '/ontology/class/list/1.0?limit=-1&offset=0',
    graphClassCount: null,
    graphTripleCount: null,
  },
  lxt: {
    health: '/health',
    propertyList: '/ontology/protege/list/1.0?limit=-1&offset=0',
    classList: '/ontology/class/list/1.0?limit=-1&offset=0',
    graphClassCount: null,
    graphTripleCount: null,
  },
  scada: {
    health: '/health',
    propertyList: '/public/en/ontology/protege/list/1.0?limit=-1&offset=0',
    classList: '/public/en/ontology/class/list/1.0?limit=-1&offset=0',
    graphClassCount: null,
    graphTripleCount: null,
  },
  tlit: {
    health: '/health',
    propertyList: '/ontology/protege/list/1.0?limit=-1&offset=0',
    classList: '/ontology/class/list/1.0?limit=-1&offset=0',
    graphClassCount: '/ontology/graph/class/count/1.0?limit=-1&offset=0',
    graphTripleCount: '/ontology/graph/triple/count/1.0?limit=-1&offset=0',
  },
  dpp: {
    health: '/health',
    propertyList: '/ontology/protege/list/1.0?limit=-1&offset=0',
    classList: '/ontology/class/list/1.0?limit=-1&offset=0',
    graphClassCount: null,
    graphTripleCount: null,
  },
  carbonFootprint: {
    health: '/health',
    propertyList: '/ontology/protege/list/1.0?limit=-1&offset=0',
    classList: '/ontology/class/list/1.0?limit=-1&offset=0',
    graphClassCount: null,
    graphTripleCount: null,
  },
  land: {
    health: '/health',
    propertyList: '/ontology/protege/list/1.0?limit=-1&offset=0',
    classList: '/ontology/class/list/1.0?limit=-1&offset=0',
    graphClassCount: null,
    graphTripleCount: null,
  },
};

const resolveOntologyEndpoint = (
  projectId: string,
  environment: OntologyEnvironment,
  endpointType: OntologyEndpointType,
): string | null => {
  if (!(projectId in endpointSuffixByProject)) {
    return null;
  }
  const key = projectId as OntologyProjectId;
  const prefix = buildProjectApiPrefix(key, environment);
  if (!prefix) {
    return null;
  }
  const suffix = endpointSuffixByProject[key][endpointType];
  if (!suffix) {
    return null;
  }
  return `${prefix}${suffix}`;
};

// 因應 easycycle-api新增權限存取限制,url 須加上 /public/{lang} 路徑
const easyApi = `${easyApiBase}/public/${langPath}`;
const tsgcApi = `${tsgcApiBase}/${langPath}`;
const nmtlApi = `${nmtlApiBase}/${langPath}`;
const hkbdbApi = `${hkbdbApiBase}/${langPath}`;
const lxtApi = `${lxtApiBase}`;
const tlitApi = `${tlitApiBase}/${langPath}`;
const dppApi = `${dppApiBase}/${langPath}`;
const carbonFootprintApi = `${carbonFootprintApiBase}/${langPath}`;
const landApi = `${landApiBase}/${langPath}`;
// const testUrl = 'http://localhost:3030';
// 預設語系
const defaultLocale = 'en';
// 當前語系
let locale = defaultLocale;

const services = {
  setLocale(lang: string) {
    locale = lang;
  },
  getDefaultLocale(): string {
    return defaultLocale;
  },
  getLocale(): string {
    return locale;
  },
  setAxiosAuth(token: string) {
    // 不加 prefix "Bearer "
    axios.defaults.headers.Authorization = `${token}`;
    axios.defaults.headers.common.Authorization = `${token}`;
  },
  resolveOntologyEndpoint,
  getOntologyEnvironmentBase(projectId: string, environment: OntologyEnvironment): string | null {
    if (!(projectId in environmentApiBases)) {
      return null;
    }
    const key = projectId as OntologyProjectId;
    return environmentApiBases[key][environment] || null;
  },
  // easy-api health
  getEasyHealth: () => `${easyApi}/health`,
  // easy-api ontology
  getEasyProtegeList: () => `${easyApi}/ontology/protege/list/1.0?limit=-1&offset=0`,
  getEasyClassList: () => `${easyApi}/ontology/class/list/1.0?limit=-1&offset=0`,
  getEasyGraphClassCount: () => `${easyApi}/ontology/graph/class/count/1.0?limit=-1&offset=0`,
  getEasyGraphTplCount: () => `${easyApi}/ontology/graph/triple/count/1.0?limit=-1&offset=0`,

  // tsgc-api health
  getTsgcHealth: () => `${tsgcApi}/health`,
  // easy-api ontology
  getTsgcProtegeList: () => `${tsgcApi}/ontology/protege/list/1.0?limit=-1&offset=0`,
  getTsgcClassList: () => `${tsgcApi}/ontology/class/list/1.0?limit=-1&offset=0`,
  getTsgcGraphClassCount: () => `${tsgcApi}/ontology/graph/class/count/1.0?limit=-1&offset=0`,
  getTsgcGraphTplCount: () => `${tsgcApi}/ontology/graph/triple/count/1.0?limit=-1&offset=0`,

  // nmtl-api health
  getNmtlHealth: () => `${nmtlApi}/health`,
  // nmtl-api ontology
  getNmtlProtege: () => `${nmtlApi}/ontology/protege/list/1.1?limit=-1&offset=0`,
  getNmtlClassList: () => `${nmtlApi}/ontology/class/list/1.0?limit=-1&offset=0`,

  // hkbdb-api health
  getHkbdbHealth: () => `${hkbdbApi}/health`,
  // hkbdb-api ontology
  getHkbdbProtege: () => `${hkbdbApi}/ontology/protege/list/2.0?limit=-1&offset=0`,
  getHkbdbClassList: () => `${hkbdbApi}/ontology/class/list/1.0?limit=-1&offset=0`,

  // lxt-api health
  // getLxtHealth: `${lxtApi}/health`,
  // lxt-api ontology
  getLxtProtege: () => `${lxtApi}/ontology/protege/list/1.0?limit=-1&offset=0`,
  getLxtClassList: () => `${lxtApi}/ontology/class/list/1.0?limit=-1&offset=0`,

  // tlit-api ontology
  getTlitHealth: () => `${tlitApi}/health`,
  // tlit-api ontology
  getTlitProtegeList: () => `${tlitApi}/ontology/protege/list/1.0?limit=-1&offset=0`,
  getTlitClassList: () => `${tlitApi}/ontology/class/list/1.0?limit=-1&offset=0`,
  getTlitGraphClassCount: () => `${tlitApi}/ontology/graph/class/count/1.0?limit=-1&offset=0`,
  getTlitGraphTplCount: () => `${tlitApi}/ontology/graph/triple/count/1.0?limit=-1&offset=0`,

  // dpp-api ontology
  getDPPProtegeList: () => `${dppApi}/ontology/protege/list/1.0?limit=-1&offset=0`,
  getDPPClassList: () => `${dppApi}/ontology/class/list/1.0?limit=-1&offset=0`,

  // carbon-footprint ontology
  getCarbonFootprintProtegeList: () => `${carbonFootprintApi}/ontology/protege/list/1.0?limit=-1&offset=0`,
  getCarbonFootprintClassList: () => `${carbonFootprintApi}/ontology/class/list/1.0?limit=-1&offset=0`,
  // TODO:若要增加專案時, 這邊要設定 api

  // land-api ontology
  getLandProtegeList: () => `${landApi}/ontology/protege/list/1.0?limit=-1&offset=0`,
  getLandClassList: () => `${landApi}/ontology/class/list/1.0?limit=-1&offset=0`,
};

export default services;
